import { useState, useEffect, useCallback } from "react";
import { SanctionsMatchReadDTO } from "../types/SanctionsMatchReadDTO";
import SanctionsApiClient from "../api/sanctionsApiClient";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";

interface SanctionsMatchFlyoutResult {
  sanctionsMatch: SanctionsMatchReadDTO | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useSanctionsMatchFlyout = (
  sanctionsMatchId?: string | null
): SanctionsMatchFlyoutResult => {
  const [sanctionsMatch, setSanctionsMatch] = useState<SanctionsMatchReadDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const sanctionsApiClient = useAuthenticatedApiClient(SanctionsApiClient);

  const fetchSanctionsMatch = useCallback(async () => {
    if (!sanctionsMatchId) {
      setSanctionsMatch(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await sanctionsApiClient.getSanctionsMatch(sanctionsMatchId);
      setSanctionsMatch(response);
    } catch (err) {
      console.error("Failed to fetch sanctions match:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch sanctions match");
      setSanctionsMatch(null);
    } finally {
      setLoading(false);
    }
  }, [sanctionsMatchId, sanctionsApiClient]);

  const refetch = useCallback(() => {
    fetchSanctionsMatch();
  }, [fetchSanctionsMatch]);

  // Fetch when sanctionsMatchId changes
  useEffect(() => {
    fetchSanctionsMatch();
  }, [fetchSanctionsMatch]);

  return {
    sanctionsMatch,
    loading,
    error,
    refetch,
  };
};
